import { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Cookies from 'js-cookie';

import { Box, Flex, Skeleton, useToken } from '@chakra-ui/react';
import { LockedIcon } from '@udacity/chakra-uds-icons';
import { headerHeight } from '@udacity/udacity-header-v2';

import { ConsumerContent } from '~/components/consumer-content';
import { ContentContainer } from '~/components/container';
import { FreeTrialPriceCard } from '~/features/agentic-ai-free-trial/components/free-trial-price-card';
import { FREE_TRIAL_LENGTH_IN_DAYS } from '~/features/agentic-ai-free-trial/utils/get-free-trial-charge-date';
import { clientHttpTrackEvent } from '~/features/analytics/client-http-track-event';
import { trackEvent } from '~/features/analytics/track-event';
import { type BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import {
  SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE,
  SUBSCRIPTION_REFERRER_PROGRAM_TITLE,
  SUBSCRIPTION_REFERRER_URL_COOKIE,
} from '~/features/cookies/constants';
import { PriceCard, StaticPriceCardProps } from '~/features/experiments/ab_plp_redesign_2025/components/price-card';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { useI18n } from '~/hooks/use-i18n';

import { DownloadGuideAsideProps, StaticDownloadGuideAsideProps } from './download-guide-aside';
import { DownloadSyllabusButton, StaticDownloadSyllabusButtonProps } from './download-syllabus-button';
import { FreeProgramCard } from './free-program-card';
import { ProgramAboutSection, StaticProgramAboutSectionProps } from './program-about-section';
import { ProgramBreadcrumbs, StaticProgramBreadcrumbsProps } from './program-bread-crumbs';
import { ProgramDetailsSection, StaticProgramDetailsSectionProps } from './program-details-section';
import { ProgramInstructorsSection, StaticProgramInstructorsSectionProps } from './program-instructors-section';
import { ProgramOutlineSection, StaticProgramOutlineSectionProps } from './program-outline-section';
import { RelatedProgramsSection, StaticRelatedProgramsSectionProps } from './related-programs-section';
import { ResumeOrSubscribeButton } from './resume-or-subscribe-button';
import { ReviewsSection, StaticReviewsSectionProps } from './reviews-section';
import { SalariesSection, StaticSalariesSectionProps } from './salaries-section';
import { SectionBar } from './section-bar';
import { IndividualSignUpButton } from './sign-up-button';
import { StartLearningCard } from './start-learning-card';
import { StaticProgramUpsellAsideProps, UpsellProgramAsideProps } from './upsell-program-aside';
import { UserExperienceCardSwitcher } from './user-experience-card-switcher';
import { WoolfMastersBannerAsideProps } from './woolf-masters-banner-aside';
import { WoolfMBABannerAsideProps } from './woolf-mba-banner-aside';

const WoolfMastersBannerAside = dynamic<WoolfMastersBannerAsideProps>(
  () => import('./woolf-masters-banner-aside').then((mod) => mod.WoolfMastersBannerAside),
  {
    ssr: false,
  }
);

const WoolfMBABannerAside = dynamic<WoolfMBABannerAsideProps>(
  () => import('./woolf-mba-banner-aside').then((mod) => mod.WoolfMBABannerAside),
  {
    ssr: false,
  }
);

const DownloadGuideAside = dynamic<DownloadGuideAsideProps>(() =>
  import('./download-guide-aside').then((res) => res.DownloadGuideAside)
);
const UpsellProgramAside = dynamic<UpsellProgramAsideProps>(() =>
  import('./upsell-program-aside').then((res) => res.UpsellProgramAside)
);

interface PriceCardEnrollButtonProps {
  selectedPlan: BillingFrequency;
  buttonLabel: string;
  programKey: string;
  programTitle: string;
}

function PriceCardEnrollButton({ selectedPlan, buttonLabel, programKey, programTitle }: PriceCardEnrollButtonProps) {
  if (!buttonLabel) {
    return <Skeleton height='3rem' width='100%' />;
  }
  let ariaLabel = '';
  let trackEventLabel = '';
  if (selectedPlan === billingFrequencySchema.enum.monthly) {
    ariaLabel = 'Sign up for the Individual "Month-To-Month" Plan';
    trackEventLabel = 'monthly_enroll_now';
  } else if (selectedPlan === billingFrequencySchema.enum['4month']) {
    ariaLabel = 'Sign up for the Individual "Four Month" Plan';
    trackEventLabel = '4month_enroll_now';
  } else if (selectedPlan === billingFrequencySchema.enum.purchase) {
    ariaLabel = `Purchase standalone access for "${programTitle}"`;
    trackEventLabel = 'onetime_purchase_enroll_now';
  }

  return (
    <IndividualSignUpButton
      ariaLabel={ariaLabel}
      billingFrequency={selectedPlan}
      overrideLabel={buttonLabel}
      programKey={programKey}
      programTitle={programTitle}
      onClick={() => {
        // Note: these cookies are required for downstream services from checkout.
        Cookies.set(SUBSCRIPTION_REFERRER_URL_COOKIE, window.location.pathname, { expires: 1 });
        Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE, programKey, { expires: 1 });
        Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_TITLE, programTitle, { expires: 1 });

        trackEvent({
          name: 'Product Page CTA Clicked',
          type: 'click',
          label: trackEventLabel,
          additionalAttributes: {
            product_ids: [programKey].filter(Boolean),
          },
        });
      }}
    />
  );
}

export type ProgramLandingPageChallengerProps = {
  programKey: string;
  imageUrl: string | null;
  companySlug?: string;
  isProgramFree?: boolean;
  staticBreadcrumbsProps: StaticProgramBreadcrumbsProps;
  staticProgramDetailsSectionProps: StaticProgramDetailsSectionProps;
  staticProgramOutlineSectionProps: StaticProgramOutlineSectionProps;
  staticProgramInstructorsSectionProps: StaticProgramInstructorsSectionProps;
  staticPriceCardProps: StaticPriceCardProps;
  staticSalariesSectionProps: StaticSalariesSectionProps;
  staticReviewsSectionProps: StaticReviewsSectionProps;
  staticProgramAboutSectionProps: StaticProgramAboutSectionProps;
  staticDownloadGuideAsideProps: StaticDownloadGuideAsideProps;
  staticDownloadSyllabusButtonProps: StaticDownloadSyllabusButtonProps;
  staticRelatedProgramsSectionProps: StaticRelatedProgramsSectionProps;
  staticUpsellProgramAsideProps: StaticProgramUpsellAsideProps;
  isMastersBannerRendered: boolean;
  isMbaBannerRendered: boolean;
  /**
   * When true, replaces the standard PriceCard (desktop sticky + mobile) with the
   * Agentic AI free-trial variant. Set only by the flag-on route at
   * `/flag/agentic-ai-ft-2026/on/course/[slug]`.
   */
  useFreeTrialPriceCard?: boolean;
};

export function ProgramLandingPageChallenger({
  companySlug,
  isProgramFree,
  programKey,
  staticBreadcrumbsProps,
  staticProgramDetailsSectionProps,
  staticProgramOutlineSectionProps,
  staticProgramInstructorsSectionProps,
  staticPriceCardProps,
  staticSalariesSectionProps,
  staticReviewsSectionProps,
  staticProgramAboutSectionProps,
  staticDownloadGuideAsideProps,
  staticDownloadSyllabusButtonProps,
  staticRelatedProgramsSectionProps,
  staticUpsellProgramAsideProps,
  imageUrl,
  isMastersBannerRendered,
  isMbaBannerRendered,
  useFreeTrialPriceCard = false,
}: ProgramLandingPageChallengerProps) {
  const { data: promotionData } = usePromotion();
  const defaultSelectedPlan = promotionData?.defaultSelectedPlan || undefined;
  return (
    <>
      <ProgramBreadcrumbs {...staticBreadcrumbsProps} />
      <StickyPriceCardTrackWrapper
        imageSlot={
          <>
            {imageUrl && (
              <Box
                borderRadius='16px'
                height='200px'
                maxWidth='356px'
                overflow='hidden'
                position='relative'
                width='100%'
                margin={{
                  base: '0 auto',
                  xl: 'unset',
                }}
                marginStart={{
                  xl: 'auto',
                }}
              >
                <Image
                  alt=''
                  priority={true}
                  quality={80}
                  sizes='356px'
                  src={imageUrl}
                  style={{
                    objectFit: 'cover',
                  }}
                  fill
                />
                {useFreeTrialPriceCard && (
                  <Box
                    backgroundColor='purple.500'
                    borderRadius='full'
                    color='white'
                    fontFamily='condensed'
                    fontSize='xs'
                    fontWeight='600'
                    left={3}
                    letterSpacing='widest'
                    paddingX={3}
                    paddingY={0.5}
                    position='absolute'
                    textTransform='uppercase'
                    top={3}
                  >
                    <Flex alignItems='center' gap={1}>
                      {FREE_TRIAL_LENGTH_IN_DAYS}-day free trial
                    </Flex>
                  </Box>
                )}
              </Box>
            )}
          </>
        }
        priceCardSlot={
          <UserExperienceCardSwitcher
            freeCardSlot={
              isProgramFree && (
                <FreeProgramCard
                  maxWidth='356px'
                  position='sticky'
                  title={staticPriceCardProps.programTitle}
                  top={`calc(${headerHeight} + 24px)`}
                  width='100%'
                  buttonSlot={
                    <ResumeOrSubscribeButton
                      programKey={staticPriceCardProps.programKey}
                      programTitle={staticPriceCardProps.programTitle}
                      semanticType={staticPriceCardProps.programSemanticType}
                      isFree
                    />
                  }
                  margin={{
                    base: '0 auto',
                    xl: 'unset',
                  }}
                  marginStart={{
                    xl: 'auto',
                  }}
                />
              )
            }
            priceCardSlot={
              useFreeTrialPriceCard ? (
                <FreeTrialPriceCard
                  maxWidth='356px'
                  position='sticky'
                  programKey={staticPriceCardProps.programKey}
                  programTitle={staticPriceCardProps.programTitle}
                  top={`calc(${headerHeight} + 24px)`}
                  width='100%'
                  margin={{
                    base: '0 auto',
                    xl: 'unset',
                  }}
                  marginStart={{
                    xl: 'auto',
                  }}
                />
              ) : (
                <PriceCard
                  {...staticPriceCardProps}
                  key={defaultSelectedPlan}
                  defaultSelectedPlan={defaultSelectedPlan}
                  maxWidth='356px'
                  position='sticky'
                  top={`calc(${headerHeight} + 24px)`}
                  width='100%'
                  margin={{
                    base: '0 auto',
                    xl: 'unset',
                  }}
                  marginStart={{
                    xl: 'auto',
                  }}
                  renderButton={(selectedPlan, buttonLabel) =>
                    companySlug ? null : (
                      <PriceCardEnrollButton
                        buttonLabel={buttonLabel}
                        programKey={staticPriceCardProps.programKey}
                        programTitle={staticPriceCardProps.programTitle}
                        selectedPlan={selectedPlan}
                      />
                    )
                  }
                />
              )
            }
            startLearningCardSlot={
              <StartLearningCard
                maxWidth='356px'
                position='sticky'
                title={staticPriceCardProps.programTitle}
                top={`calc(${headerHeight} + 24px)`}
                width='100%'
                buttonSlot={
                  <ResumeOrSubscribeButton
                    isFree={false}
                    programKey={staticPriceCardProps.programKey}
                    programTitle={staticPriceCardProps.programTitle}
                    semanticType={staticPriceCardProps.programSemanticType}
                  />
                }
                margin={{
                  base: '0 auto',
                  xl: 'unset',
                }}
                marginStart={{
                  xl: 'auto',
                }}
              />
            }
          />
        }
      >
        <Flex
          flexDirection='column'
          gap={14}
          padding={{
            base: '24px 0px',
            md: '48px 0px',
          }}
        >
          <ProgramDetailsSection
            {...staticProgramDetailsSectionProps}
            priceCardSlot={
              <>
                {imageUrl && (
                  <Box
                    borderRadius='16px'
                    height='200px'
                    margin='0 auto -60px'
                    maxWidth='100%'
                    overflow='hidden'
                    position='relative'
                    width='100%'
                    display={{
                      base: 'block',
                      lg: 'none',
                    }}
                  >
                    <Image
                      alt=''
                      priority={true}
                      quality={80}
                      sizes='356px'
                      src={imageUrl}
                      style={{
                        objectFit: 'cover',
                      }}
                      fill
                    />
                    {useFreeTrialPriceCard && (
                      <Box
                        backgroundColor='purple.500'
                        borderRadius='full'
                        color='white'
                        fontFamily='condensed'
                        fontSize='xs'
                        fontWeight='600'
                        left={3}
                        letterSpacing='widest'
                        paddingX={3}
                        paddingY={0.5}
                        position='absolute'
                        textTransform='uppercase'
                        top={3}
                      >
                        <Flex alignItems='center' gap={1}>
                          <LockedIcon height='12px' width='12px' />
                          {FREE_TRIAL_LENGTH_IN_DAYS}-day free trial
                        </Flex>
                      </Box>
                    )}
                  </Box>
                )}
                <UserExperienceCardSwitcher
                  freeCardSlot={
                    isProgramFree && (
                      <FreeProgramCard
                        title={staticPriceCardProps.programTitle}
                        width='100%'
                        buttonSlot={
                          <ResumeOrSubscribeButton
                            programKey={staticPriceCardProps.programKey}
                            programTitle={staticPriceCardProps.programTitle}
                            semanticType={staticPriceCardProps.programSemanticType}
                            isFree
                          />
                        }
                        display={{
                          base: 'flex',
                          lg: 'none',
                        }}
                        isMobile
                      />
                    )
                  }
                  priceCardSlot={
                    useFreeTrialPriceCard ? (
                      <FreeTrialPriceCard
                        isolation='isolate'
                        margin='0 auto 24px'
                        maxWidth='100%'
                        programKey={staticPriceCardProps.programKey}
                        programTitle={staticPriceCardProps.programTitle}
                        width='100%'
                        display={{
                          base: 'flex',
                          lg: 'none',
                        }}
                        isMobile
                      />
                    ) : (
                      <PriceCard
                        {...staticPriceCardProps}
                        key={defaultSelectedPlan}
                        defaultSelectedPlan={defaultSelectedPlan}
                        isolation='isolate'
                        margin='0 auto 24px'
                        maxWidth='100%'
                        width='100%'
                        display={{
                          base: 'flex',
                          lg: 'none',
                        }}
                        renderButton={(selectedPlan, buttonLabel) =>
                          companySlug ? null : (
                            <PriceCardEnrollButton
                              buttonLabel={buttonLabel}
                              programKey={staticPriceCardProps.programKey}
                              programTitle={staticPriceCardProps.programTitle}
                              selectedPlan={selectedPlan}
                            />
                          )
                        }
                        isMobile
                      />
                    )
                  }
                  startLearningCardSlot={
                    <StartLearningCard
                      isolation='isolate'
                      margin='0 auto 24px'
                      maxWidth='100%'
                      title={staticPriceCardProps.programTitle}
                      width='100%'
                      buttonSlot={
                        <ResumeOrSubscribeButton
                          isFree={false}
                          programKey={staticPriceCardProps.programKey}
                          programTitle={staticPriceCardProps.programTitle}
                          semanticType={staticPriceCardProps.programSemanticType}
                        />
                      }
                      display={{
                        base: 'flex',
                        lg: 'none',
                      }}
                      isMobile
                    />
                  }
                />
              </>
            }
          />
          {isMastersBannerRendered && <WoolfMastersBannerAside programKey={programKey} />}
          {isMbaBannerRendered && <WoolfMBABannerAside programKey={programKey} />}
          <SectionBar />
          {staticDownloadSyllabusButtonProps && (
            <DownloadSyllabusButton
              {...staticDownloadSyllabusButtonProps}
              onAuthClick={(user) => {
                clientHttpTrackEvent({
                  type: 'submit',
                  name: 'Nanodegree Interested',
                  label: `Nanodegree Interested - ${programKey} - STANDARD`,
                  additionalAttributes: {
                    product_ids: [programKey],
                    firstName: user?.firstName,
                    phone_number: user?.phoneNumber,
                    last_name: user?.lastName,
                    interested_in: 'syllabus',
                  },
                });
              }}
              onClick={() =>
                clientHttpTrackEvent({
                  name: 'Product Page CTA Clicked',
                  type: 'click',
                  label: 'syllabus_cta_clicked',
                  additionalAttributes: {
                    product_ids: [programKey],
                  },
                })
              }
              onFormSubmit={(formData) =>
                clientHttpTrackEvent({
                  type: 'submit',
                  name: 'Nanodegree Interested',
                  label: `Nanodegree Interested - ${programKey} - STANDARD`,
                  additionalAttributes: {
                    product_ids: [programKey],
                    firstName: formData.firstName,
                    phone_number: formData.phone,
                    last_name: formData.lastName,
                    interested_in: 'syllabus',
                  },
                })
              }
            />
          )}
          {staticProgramOutlineSectionProps && (
            <>
              <ProgramOutlineSection {...staticProgramOutlineSectionProps} />
              <SectionBar />
            </>
          )}

          {staticSalariesSectionProps && (
            <ConsumerContent>
              <SalariesSection {...staticSalariesSectionProps} />
              <SectionBar />
            </ConsumerContent>
          )}

          {staticProgramInstructorsSectionProps && (
            <>
              <ProgramInstructorsSection {...staticProgramInstructorsSectionProps} />
              <SectionBar />
            </>
          )}

          {staticUpsellProgramAsideProps && (
            <ConsumerContent>
              <ContentContainer>
                <UpsellProgramAside {...staticUpsellProgramAsideProps} />
              </ContentContainer>
              <SectionBar />
            </ConsumerContent>
          )}

          {staticReviewsSectionProps && (
            <>
              <ReviewsSection {...staticReviewsSectionProps} />
              <SectionBar />
            </>
          )}

          {staticDownloadGuideAsideProps && (
            <>
              <DownloadGuideAside {...staticDownloadGuideAsideProps} />
              <SectionBar />
            </>
          )}

          {staticProgramAboutSectionProps && (
            <>
              <ProgramAboutSection {...staticProgramAboutSectionProps} />
              <SectionBar />
            </>
          )}
        </Flex>
      </StickyPriceCardTrackWrapper>
      {staticRelatedProgramsSectionProps && <RelatedProgramsSection {...staticRelatedProgramsSectionProps} />}
    </>
  );
}

function StickyPriceCardTrackWrapper({
  children,
  priceCardSlot,
  imageSlot,
}: {
  children: ReactNode;
  priceCardSlot: ReactNode;
  imageSlot: ReactNode;
}) {
  const [contentMaxWidth] = useToken('sizes', ['content-max-width']);
  const TRACK_WIDTH_PIXELS = '404px';
  const { isRtl } = useI18n();
  return (
    <Box
      position='relative'
      sx={{
        '--track-padding-end': TRACK_WIDTH_PIXELS,
        '--carousel-container-padding-end': TRACK_WIDTH_PIXELS,
      }}
    >
      {children}
      <Box
        bottom={0}
        maxWidth={TRACK_WIDTH_PIXELS}
        paddingBottom={6}
        position='absolute'
        top='48px'
        width='100%'
        display={{
          base: 'none',
          lg: 'block',
        }}
        {...(isRtl
          ? {
              left: {
                base: 0,
                xl: `calc((100% - ${contentMaxWidth})/2)`,
              },
            }
          : {
              right: {
                base: 0,
                xl: `calc((100% - ${contentMaxWidth})/2)`,
              },
            })}
      >
        {imageSlot}
        <Box
          bottom={0}
          maxWidth={TRACK_WIDTH_PIXELS}
          paddingBottom={12}
          position='absolute'
          top='165px'
          width='100%'
          display={{
            base: 'none',
            lg: 'block',
          }}
        >
          {priceCardSlot}
        </Box>
      </Box>
    </Box>
  );
}
